import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { palette, spacing, typography } from '../../../style-utils/theme';
import * as api from '../../../api/admin-client';
import { useAdminList, useAdminResource } from './use-admin';
import { AdminTable, Column, Id, Tag, StatusTag, labelFor, toneFor, when } from './admin-table';
import { deviceZone, toZonedInputValue, zonedInputToIso } from '../../../function-utils/building-time';
import { ActionHost } from './admin-details';
import { AdminPanel as Panel, AdminFacts as Facts, AdminLoading as Loading } from './admin-screens';
import {
  Card,
  CardHead,
  CardTitle,
  CardBody,
  Toolbar,
  Select,
  SearchBox,
  Btn,
  Input,
  Field,
  FieldLabel,
  FieldHint,
  FieldGrid,
  ErrorBox,
  NoteBox,
  Mono,
} from './admin.styled';

/**
 * PROMOTIONS — the Super Admin screens.
 *
 * ── NOTHING ON THIS SCREEN DECIDES ANYTHING ────────────────────────────────
 *
 * Not the status word, not the usage count, not whether a field may be edited,
 * and not what a promotion is worth. Every one of those is computed by the
 * server and rendered here: `status` comes out of SQL (a browser deriving it
 * from `expires_at` would disagree with the filter on the next screen),
 * `redemptions_used` comes from the ledger, and an edit the server will refuse
 * is refused BY THE SERVER — this form does not try to predict it.
 *
 * What the form does do is stop an operator submitting something obviously
 * incomplete, which is courtesy, not validation. The 400 that comes back names
 * the real rule, and it is shown verbatim.
 *
 * ── WHY A SEPARATE FILE ────────────────────────────────────────────────────
 *
 * admin-screens.tsx holds column specs — fifteen lines each — and that is why
 * one file is right for them. This is a form with eleven fields, a detail page
 * and five audited actions; putting it in there would make the file that is
 * currently "every column in the console" into something else.
 *
 * ── THERE IS NO DELETE BUTTON, DELIBERATELY ────────────────────────────────
 *
 * Deactivate is reversible and Archive is not, and neither destroys anything.
 * The backend holds no DELETE privilege on either promo table, so a delete
 * control here would be a button that cannot work — and a promotion whose
 * redemptions outlived it is a billing record nobody can explain.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const pct = (v: unknown) => `${Number(v)}%`;
const money = (cents: unknown) =>
  cents == null
    ? '—'
    : (Number(cents) / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/** How long the discount lasts, in the words the customer is shown. */
/* MEASURED, not assumed (scripts/iq/100b-promo-lifecycle.js §1): a repeating
   coupon runs for N CALENDAR MONTHS from the moment the discount is attached,
   and Stripe discounts every invoice raised inside that window. It is not "N
   invoices", and on a new signup the 30-day trial sits inside the window. The
   label says months because months is what it is. */
const durationLabel = (r: Row): string =>
  r.duration === 'forever'
    ? 'Forever'
    : r.duration === 'once'
      ? 'First payment only'
      : `${r.duration_in_months} month${r.duration_in_months === 1 ? '' : 's'} from first use`;

const intervalLabel = (v: string) =>
  v === 'both' ? 'Monthly + annual' : v === 'year' ? 'Annual only' : 'Monthly only';

const eligibilityLabel = (v: string) =>
  v === 'new' ? 'New customers' : v === 'existing' ? 'Existing customers' : 'Anyone';

/* A promotion's status is a server word; this only picks its colour. */
const statusTone = (s: string) =>
  s === 'active' ? 'success' : s === 'archived' ? 'neutral' : s === 'expired' ? 'alert' : 'warning';

export const PROMO_COLUMNS: Column<Row>[] = [
  { key: 'code', header: 'Code', render: r => <Mono>{r.code}</Mono> },
  {
    key: 'status',
    header: 'Status',
    render: r => <Tag tone={statusTone(r.status) as any}>{r.status}</Tag>,
  },
  { key: 'discount', header: 'Discount', numeric: true, render: r => pct(r.discount_percent) },
  { key: 'duration', header: 'Lasts', render: r => durationLabel(r) },
  {
    key: 'interval',
    header: 'Billing',
    render: r => <Tag tone="brand">{intervalLabel(r.applies_to_interval)}</Tag>,
  },
  {
    key: 'usage',
    header: 'Used',
    numeric: true,
    render: r => (
      <span title={`${r.redemptions_confirmed ?? 0} confirmed by a paid invoice`}>
        {Number(r.redemptions_used ?? 0)}
        <span style={{ color: palette.slateLight }}>
          {' / '}
          {r.max_redemptions == null ? '∞' : r.max_redemptions}
        </span>
      </span>
    ),
  },
  {
    key: 'window',
    header: 'Window',
    wide: true,
    render: r =>
      r.starts_at || r.expires_at ? (
        <span>
          {r.starts_at ? when(r.starts_at) : 'now'} → {r.expires_at ? when(r.expires_at) : 'no end'}
        </span>
      ) : (
        <span style={{ color: palette.slateLight }}>always</span>
      ),
  },
  { key: 'by', header: 'Created by', wide: true, render: r => r.created_by_label || '—' },
  { key: 'created', header: 'Created', wide: true, render: r => when(r.created_at) },
];

export const REDEMPTION_COLUMNS: Column<Row>[] = [
  { key: 'code', header: 'Code', render: r => <Mono>{r.code}</Mono> },
  {
    key: 'org',
    header: 'Organization',
    /* The building under it. A self-serve signup's organisation is named
       "Organization <uuid>" (the wizard never asks for a company name), which
       tells an operator nothing — the building they typed does. */
    render: r => (
      <span>
        {r.organization_name || <Id value={r.organization_id} />}
        {r.building_name ? (
          <span style={{ display: 'block', color: palette.slate, fontSize: typography.size.xxs }}>
            {r.building_name}
          </span>
        ) : null}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    /* `confirmed` is the only one that means money actually moved. The tooltip
       says so, because four words that look alike on a screen are four words
       nobody can tell apart six months later. */
    render: r => (
      <StatusTag value={r.status} tone={toneFor(r.status === 'confirmed' ? 'active' : r.status)} />
    ),
  },
  { key: 'plan', header: 'Plan', render: r => labelFor(r.plan_code) },
  { key: 'interval', header: 'Billing', render: r => r.billing_interval || '—' },
  { key: 'discount', header: 'Discount', numeric: true, render: r => pct(r.discount_percent) },
  {
    key: 'worth',
    header: 'Taken off',
    numeric: true,
    render: r =>
      r.confirmed_discount_cents == null ? (
        <span style={{ color: palette.slateLight }}>not yet invoiced</span>
      ) : (
        money(r.confirmed_discount_cents)
      ),
  },
  {
    key: 'sub',
    header: 'Subscription',
    wide: true,
    render: r => (r.stripe_subscription_id ? <Mono>{r.stripe_subscription_id}</Mono> : '—'),
  },
  { key: 'when', header: 'Redeemed', render: r => when(r.created_at) },
  {
    key: 'why',
    header: 'Released because',
    wide: true,
    render: r => r.release_reason || <span style={{ color: palette.slateLight }}>—</span>,
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   THE CREATE FORM
   ═══════════════════════════════════════════════════════════════════════════ */

const EMPTY = {
  code: '',
  description: '',
  discount_percent: '',
  duration: 'forever',
  duration_in_months: '',
  applies_to_interval: '',
  customer_eligibility: 'all',
  max_redemptions: '',
  max_per_customer: '1',
  starts_at: '',
  expires_at: '',
};

/** A `datetime-local` value is a WALL CLOCK with no zone: read on the
 *  OPERATOR's own clock (R-T6 — a promo code belongs to no building) and sent
 *  as an instant. Through the one time module, with the zone named. */
const toInstant = (v: string): string | null =>
  v ? zonedInputToIso(v, deviceZone()) || null : null;

/* THE OTHER DIRECTION, in the operator's zone. The edit panel used to prefill
   with `toISOString().slice(0, 16)` — the UTC wall clock — and save whatever
   was in the box as LOCAL time, so "move it one day later" shifted the expiry
   by the operator's UTC offset as well (measured: 18h30 in India, 31h in Los
   Angeles, instead of 24h). What the box shows is now what it saves. */
const toLocalInput = (iso?: string | null): string =>
  iso ? toZonedInputValue(iso, deviceZone()) : '';

/* A whole-number field as the server should see it: blank is null (unlimited),
   anything else goes as TYPED, so a typo is refused by the server's validator
   rather than becoming NaN -> null -> "unlimited" in the browser. */
const limitOut = (v: string): string | null => (v.trim() === '' ? null : v.trim());

const NOTE_MAX = 200;

/** Which plans a promotion may be used on. None ticked = every plan. */
const PlanPicker: React.FC<{
  id: string;
  value: number[];
  onChange: (next: number[]) => void;
}> = ({ id, value, onChange }) => {
  const [plans, setPlans] = useState<api.PromoPlan[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    api
      .getPromoPlans()
      .then(r => {
        if (live) setPlans(r.items);
      })
      .catch(() => {
        if (live) setFailed(true);
      });

    return () => {
      live = false;
    };
  }, []);
  if (failed) return <FieldHint>Could not load the plans.</FieldHint>;
  if (!plans) return <FieldHint>Loading plans…</FieldHint>;

  // Grouped by product (migration 176): a promotion is for Desk plans or for
  // Stays plans, never both (the API refuses a mix).
  const groups = [
    { key: 'desk', label: 'Desk', items: plans.filter(pl => (pl.product_line || 'desk') === 'desk') },
    { key: 'stays', label: 'Stays', items: plans.filter(pl => pl.product_line === 'stays') },
  ].filter(g => g.items.length);

  return (
    <div id={id} role="group" style={{ display: 'grid', gap: spacing.xs }}>
      {groups.map(g => (
        <div key={g.key} data-plan-group={g.key} style={{ display: 'flex', flexWrap: 'wrap', gap: `4px ${spacing.sm}` }}>
          {groups.length > 1 && <strong style={{ flexBasis: '100%' }}>{g.label}</strong>}
          {g.items.map(pl => (
            <label key={pl.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                value={pl.id}
                data-plan-code={pl.code}
                checked={value.includes(pl.id)}
                onChange={e =>
                  onChange(e.target.checked ? [...value, pl.id] : value.filter(x => x !== pl.id))
                }
              />
              {pl.name} ({pl.billing_interval === 'year' ? 'annual' : 'monthly'})
            </label>
          ))}
        </div>
      ))}
    </div>
  );
};

const NewPromoForm: React.FC<{ onCreated: () => void; requestAction: ActionHost['requestAction'] }> = ({
  onCreated,
  requestAction,
}) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ ...EMPTY });
  const [planIds, setPlanIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF(p => ({ ...p, [k]: e.target.value }));

  /* A temporary promotion has NO default interval, on the server and therefore
     here. The reason is measured: Stripe applies a repeating coupon to every
     invoice inside its window, and one ANNUAL invoice falls inside a
     three-month window — so "20% for 3 months" left unattended takes 20% off a
     whole year. The operator chooses, at the moment they are thinking about it. */
  const temporary = f.duration !== 'forever';
  const ready =
    f.code.trim().length >= 3 &&
    f.discount_percent !== '' &&
    (!temporary || f.applies_to_interval !== '') &&
    (f.duration !== 'repeating' || f.duration_in_months !== '');

  const submit = () => {
    setError(null);
    requestAction({
      title: `Create promotion ${f.code.trim().toUpperCase()}`,
      body: (
        <>
          This creates a real coupon at our payment provider. Anyone holding the code will get{' '}
          <strong>
            {f.discount_percent}% off{' '}
            {f.duration === 'forever'
              ? 'every payment'
              : f.duration === 'once'
                ? 'their first payment'
                : `for ${f.duration_in_months} months`}
          </strong>
          {f.applies_to_interval && f.applies_to_interval !== 'both'
            ? ` on ${f.applies_to_interval === 'year' ? 'annual' : 'monthly'} billing`
            : ''}
          .
        </>
      ),
      confirmLabel: 'Create promotion',
      onConfirm: async reason => {
        try {
          await api.createPromo(
            {
              code: f.code.trim(),
              description: f.description.trim() || null,
              discount_percent: f.discount_percent,
              duration: f.duration,
              duration_in_months: f.duration === 'repeating' ? limitOut(f.duration_in_months) : null,
              ...(f.applies_to_interval ? { applies_to_interval: f.applies_to_interval } : {}),
              plan_ids: planIds.length ? planIds : null,
              customer_eligibility: f.customer_eligibility,
              max_redemptions: limitOut(f.max_redemptions),
              max_per_customer: limitOut(f.max_per_customer),
              starts_at: toInstant(f.starts_at),
              expires_at: toInstant(f.expires_at),
            },
            reason
          );
          toast.success('Promotion created');
          setF({ ...EMPTY });
          setPlanIds([]);
          setOpen(false);
          onCreated();
        } catch (e: any) {
          /* THE SERVER'S OWN SENTENCE, verbatim. It names the rule that was
             broken; a generic "could not create" here would hide it. */
          const msg = e?.response?.data?.message || 'Could not create the promotion.';
          setError(msg);
          throw e;
        }
      },
    });
  };

  if (!open) {
    return (
      <Card>
        <CardHead>
          <CardTitle>Promotions</CardTitle>
          <Btn variant="primary" onClick={() => setOpen(true)}>
            New promotion
          </Btn>
        </CardHead>
      </Card>
    );
  }

  return (
    <Card>
      <CardHead>
        <CardTitle>New promotion</CardTitle>
        <Btn onClick={() => setOpen(false)}>Cancel</Btn>
      </CardHead>
      <CardBody>
        {error ? <ErrorBox role="alert">{error}</ErrorBox> : null}
        <FieldGrid style={{ marginTop: error ? spacing.sm : 0 }}>
          <Field>
            <FieldLabel>Code</FieldLabel>
            <Input
              id="promo-code"
              value={f.code}
              onChange={set('code')}
              placeholder="SPRING25"
              autoCapitalize="characters"
            />
            <FieldHint>Stored and matched in upper case.</FieldHint>
          </Field>
          <Field>
            <FieldLabel>Discount %</FieldLabel>
            <Input
              id="promo-percent"
              value={f.discount_percent}
              onChange={set('discount_percent')}
              inputMode="decimal"
              placeholder="20"
            />
            <FieldHint>Above 0, up to 100. Two decimals.</FieldHint>
          </Field>
          <Field>
            <FieldLabel>Lasts</FieldLabel>
            <Select id="promo-duration" value={f.duration} onChange={set('duration')}>
              <option value="forever">Forever</option>
              <option value="once">First payment only</option>
              <option value="repeating">A number of months</option>
            </Select>
          </Field>
          {f.duration === 'repeating' ? (
            <Field>
              <FieldLabel>For how many months</FieldLabel>
              <Input
                id="promo-months"
                value={f.duration_in_months}
                onChange={set('duration_in_months')}
                inputMode="numeric"
                placeholder="3"
              />
              <FieldHint>
                Calendar months from when the code is applied, up to 120 (ten
                years) — there is no separate years field, so two years is 24. A
                new signup&rsquo;s 30-day trial sits inside that window, so on a
                monthly plan 3 months discounts two or three invoices, depending
                on how long the months are; choose 4 for at least three. After
                the window the customer pays full price.
              </FieldHint>
            </Field>
          ) : null}
          <Field>
            <FieldLabel>Billing intervals</FieldLabel>
            <Select
              id="promo-interval"
              value={f.applies_to_interval}
              onChange={set('applies_to_interval')}
            >
              <option value="">{temporary ? 'Choose one…' : 'Monthly + annual'}</option>
              <option value="month">Monthly only</option>
              <option value="year">Annual only</option>
              <option value="both">Monthly + annual</option>
            </Select>
            {temporary ? (
              <FieldHint>
                Required. A limited-time discount on an annual plan discounts a whole year.
              </FieldHint>
            ) : null}
          </Field>
          <Field>
            <FieldLabel>Who may use it</FieldLabel>
            <Select
              id="promo-eligibility"
              value={f.customer_eligibility}
              onChange={set('customer_eligibility')}
            >
              <option value="all">Anyone</option>
              <option value="new">New customers only</option>
              <option value="existing">Existing customers only</option>
            </Select>
            <FieldHint>New = we have never taken a payment from them.</FieldHint>
          </Field>
          <Field>
            <FieldLabel>Maximum redemptions</FieldLabel>
            <Input
              id="promo-max"
              value={f.max_redemptions}
              onChange={set('max_redemptions')}
              inputMode="numeric"
              placeholder="Unlimited"
            />
            <FieldHint>
              The cap on the whole promotion — 20 means the first 20 customers who
              actually use it. Blank for unlimited.
            </FieldHint>
          </Field>
          <Field>
            <FieldLabel>Maximum per customer</FieldLabel>
            <Input
              id="promo-max-customer"
              value={f.max_per_customer}
              onChange={set('max_per_customer')}
              inputMode="numeric"
              placeholder="Unlimited"
            />
            <FieldHint>Blank for unlimited.</FieldHint>
          </Field>
          <Field>
            <FieldLabel>Starts</FieldLabel>
            <Input
              id="promo-starts"
              type="datetime-local"
              value={f.starts_at}
              onChange={set('starts_at')}
            />
            <FieldHint>Your local time. Blank = immediately.</FieldHint>
          </Field>
          <Field>
            <FieldLabel>Expires</FieldLabel>
            <Input
              id="promo-expires"
              type="datetime-local"
              value={f.expires_at}
              onChange={set('expires_at')}
            />
            <FieldHint>Your local time. Blank = never.</FieldHint>
          </Field>
          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>Plans</FieldLabel>
            <PlanPicker id="promo-plans" value={planIds} onChange={setPlanIds} />
            <FieldHint>
              Leave all unticked for every Desk plan. Stays plans must be ticked. A customer
              who moves to a plan that is not ticked here loses the discount.
            </FieldHint>
          </Field>
          <Field style={{ gridColumn: '1 / -1' }}>
            <FieldLabel>Internal note</FieldLabel>
            <Input
              id="promo-description"
              value={f.description}
              onChange={set('description')}
              maxLength={NOTE_MAX}
              placeholder="What this campaign is for. Never shown to a customer."
            />
            <FieldHint>
              {f.description.length}/{NOTE_MAX} characters.
            </FieldHint>
          </Field>
        </FieldGrid>
        <Toolbar style={{ marginTop: spacing.sm }}>
          <Btn variant="primary" disabled={!ready} onClick={submit} id="promo-create">
            Create promotion
          </Btn>
          <span style={{ fontSize: typography.size.xxs, color: palette.slate }}>
            You will be asked for your password and a reason.
          </span>
        </Toolbar>
      </CardBody>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   THE LIST
   ═══════════════════════════════════════════════════════════════════════════ */

export const PromosScreen: React.FC<ActionHost> = ({ requestAction }) => {
  const nav = useNavigate();
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [nonce, setNonce] = useState(0);

  const pickedKey = JSON.stringify(picked);
  const params = useMemo(
    () => ({ ...(JSON.parse(pickedKey) as Record<string, string>), ...(term ? { q: term } : {}), nonce }),
    [pickedKey, term, nonce]
  );
  const state = useAdminList<Row>('promos', params, { debounce: true });

  return (
    <>
      <NoteBox>
        A promotion is created here and <strong>owns</strong> a coupon at our payment provider.
        Every rule — the window, the limits, which billing intervals it applies to and who may use
        it — is enforced by our server; the provider only works out what the discount is worth.
        Nothing can be deleted: switch a promotion off to stop it, or archive it to retire it for
        good.
      </NoteBox>

      <NewPromoForm onCreated={() => setNonce(x => x + 1)} requestAction={requestAction} />

      <Card>
        <CardHead>
          <Toolbar>
            <SearchBox
              value={term}
              onChange={e => setTerm(e.target.value)}
              placeholder="Code or note…"
              aria-label="Filter promotions"
            />
            {[
              {
                name: 'status',
                label: 'Status',
                options: ['active', 'inactive', 'upcoming', 'expired', 'archived'],
              },
              { name: 'interval', label: 'Billing', options: ['month', 'year', 'both'] },
            ].map(f => (
              <label key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: typography.size.xxs, color: palette.slate }}>{f.label}</span>
                <Select
                  value={picked[f.name] ?? ''}
                  onChange={e =>
                    setPicked(p => {
                      const next = { ...p };
                      if (e.target.value) next[f.name] = e.target.value;
                      else delete next[f.name];

                      return next;
                    })
                  }
                >
                  <option value="">All</option>
                  {f.options.map(o => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </Select>
              </label>
            ))}
          </Toolbar>
          <Btn onClick={state.reload}>Refresh</Btn>
        </CardHead>
        <CardBody>
          <AdminTable<Row>
            columns={PROMO_COLUMNS}
            rows={state.items}
            rowKey={r => String(r.id)}
            loading={state.loading}
            loadingMore={state.loadingMore}
            error={state.error}
            total={state.total}
            hasMore={state.hasMore}
            onLoadMore={state.loadMore}
            emptyLabel="No promotions yet."
            onRowClick={r => nav(`/admin/promos/${r.id}`)}
          />
        </CardBody>
      </Card>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ONE PROMOTION
   ═══════════════════════════════════════════════════════════════════════════ */

export const PromoDetail: React.FC<ActionHost> = ({ requestAction }) => {
  const { id } = useParams();
  const nav = useNavigate();
  const { data, loading, error, reload, notFound } = useAdminResource<{
    promo: api.Promo;
    plans: { id: number; code: string; name: string }[];
    redemptions: Row[];
  }>(id ? `/promos/${id}` : null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState<Record<string, string>>({});
  const [editPlans, setEditPlans] = useState<number[] | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (notFound) return <ErrorBox role="alert">No such promotion.</ErrorBox>;
  if (loading || !data) return <Loading />;

  const p = data.promo;
  /* The server decides this too — it refuses the edit with
     PROMO_DISCOUNT_IMMUTABLE. The screen only avoids offering a field that
     cannot move. */
  const redeemed = Number(p.redemptions_total ?? 0) > 0;

  const act = (
    title: string,
    body: React.ReactNode,
    confirmLabel: string,
    run: (reason: string) => Promise<unknown>,
    danger = false
  ) =>
    requestAction({
      title,
      body,
      confirmLabel,
      danger,
      onConfirm: async reason => {
        await run(reason);
        toast.success(confirmLabel);
        reload();
      },
    });

  return (
    <>
      <Toolbar style={{ marginBottom: spacing.sm }}>
        <Btn onClick={() => nav('/admin/promos')}>← Promotions</Btn>
      </Toolbar>

      <Panel title={p.code}>
        <Facts
          rows={[
            ['Status', <Tag key="status" tone={statusTone(p.status) as any}>{p.status}</Tag>],
            ['Customers see', p.description_public],
            ['Discount', pct(p.discount_percent)],
            ['Lasts', durationLabel(p as unknown as Row)],
            ['Billing intervals', intervalLabel(p.applies_to_interval)],
            [
              'Plans',
              data.plans.length ? data.plans.map(x => x.code).join(', ') : 'Every purchasable plan',
            ],
            ['Who may use it', eligibilityLabel(p.customer_eligibility)],
            [
              'Redemptions',
              `${p.redemptions_used ?? 0} held${
                p.max_redemptions == null ? '' : ` of ${p.max_redemptions}`
              } · ${p.redemptions_confirmed ?? 0} confirmed by a paid invoice`,
            ],
            ['Per customer', p.max_per_customer == null ? 'Unlimited' : String(p.max_per_customer)],
            ['Starts', p.starts_at ? when(p.starts_at) : 'Immediately'],
            ['Expires', p.expires_at ? when(p.expires_at) : 'Never'],
            ['Created by', `${p.created_by_label} · ${when(p.created_at)}`],
            ...(p.updated_by_label
              ? ([['Last edited by', `${p.updated_by_label} · ${when(p.updated_at)}`]] as [
                  string,
                  React.ReactNode,
                ][])
              : []),
            ['Coupon', <Mono key="coupon">{p.stripe_coupon_id}</Mono>],
            ['Promotion code', <Mono key="pc">{p.stripe_promotion_code_id}</Mono>],
          ]}
        />
        <Toolbar style={{ marginTop: spacing.sm }}>
          {p.archived_at ? null : p.is_active ? (
            <Btn
              id="promo-deactivate"
              onClick={() =>
                act(
                  `Switch off ${p.code}`,
                  <>Nobody will be able to use this code. Discounts already running are unaffected.</>,
                  'Switch it off',
                  reason => api.deactivatePromo(p.id, reason)
                )
              }
            >
              Deactivate
            </Btn>
          ) : (
            <Btn
              id="promo-activate"
              onClick={() =>
                act(
                  `Switch on ${p.code}`,
                  <>The code will work again immediately.</>,
                  'Switch it on',
                  reason => api.activatePromo(p.id, reason)
                )
              }
            >
              Reactivate
            </Btn>
          )}
          {p.archived_at ? null : (
            <Btn onClick={() => setEditing(v => !v)} id="promo-edit">
              {editing ? 'Stop editing' : 'Edit'}
            </Btn>
          )}
          {p.archived_at ? null : (
            <Btn
              variant="danger"
              id="promo-archive"
              onClick={() =>
                act(
                  `Archive ${p.code}`,
                  <>
                    Archiving retires the promotion for good — it cannot be switched back on. Its
                    redemption history is kept. To stop it temporarily, deactivate it instead.
                  </>,
                  'Archive it',
                  reason => api.archivePromo(p.id, reason),
                  true
                )
              }
            >
              Archive
            </Btn>
          )}
        </Toolbar>
      </Panel>

      {editing ? (
        <Panel title="Edit">
          {editError ? <ErrorBox role="alert">{editError}</ErrorBox> : null}
          <NoteBox>
            {redeemed
              ? 'This promotion has been redeemed, so its discount and duration are fixed. Everything below governs FUTURE redemptions only — nothing reaches a discount that is already running.'
              : 'Nobody has used this promotion yet, so its discount can still be changed. Doing so re-issues the coupon at our payment provider under the same code.'}
          </NoteBox>
          <FieldGrid style={{ marginTop: spacing.sm }}>
            {redeemed ? null : (
              <Field>
                <FieldLabel>Discount %</FieldLabel>
                <Input
                  id="promo-edit-percent"
                  defaultValue={String(p.discount_percent)}
                  onChange={e => setEdit(s => ({ ...s, discount_percent: e.target.value }))}
                  inputMode="decimal"
                />
              </Field>
            )}
            {redeemed ? null : (
              <Field>
                <FieldLabel>Lasts</FieldLabel>
                <Select
                  id="promo-edit-duration"
                  defaultValue={p.duration}
                  onChange={e => setEdit(s => ({ ...s, duration: e.target.value }))}
                >
                  <option value="forever">Forever</option>
                  <option value="once">First payment only</option>
                  <option value="repeating">A number of months</option>
                </Select>
              </Field>
            )}
            {!redeemed && (edit.duration ?? p.duration) === 'repeating' ? (
              <Field>
                <FieldLabel>For how many months</FieldLabel>
                <Input
                  id="promo-edit-months"
                  defaultValue={p.duration_in_months == null ? '' : String(p.duration_in_months)}
                  onChange={e => setEdit(s => ({ ...s, duration_in_months: e.target.value }))}
                  inputMode="numeric"
                />
              </Field>
            ) : null}
            <Field>
              <FieldLabel>Billing intervals</FieldLabel>
              <Select
                id="promo-edit-interval"
                defaultValue={p.applies_to_interval}
                onChange={e => setEdit(s => ({ ...s, applies_to_interval: e.target.value }))}
              >
                <option value="month">Monthly only</option>
                <option value="year">Annual only</option>
                <option value="both">Monthly + annual</option>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Who may use it</FieldLabel>
              <Select
                id="promo-edit-eligibility"
                defaultValue={p.customer_eligibility}
                onChange={e => setEdit(s => ({ ...s, customer_eligibility: e.target.value }))}
              >
                <option value="all">Anyone</option>
                <option value="new">New customers only</option>
                <option value="existing">Existing customers only</option>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Maximum redemptions</FieldLabel>
              <Input
                id="promo-edit-max"
                defaultValue={p.max_redemptions == null ? '' : String(p.max_redemptions)}
                placeholder="Unlimited"
                onChange={e => setEdit(s => ({ ...s, max_redemptions: e.target.value }))}
                inputMode="numeric"
              />
              <FieldHint>Cannot go below what has already been used.</FieldHint>
            </Field>
            <Field>
              <FieldLabel>Maximum per customer</FieldLabel>
              <Input
                id="promo-edit-max-customer"
                defaultValue={p.max_per_customer == null ? '' : String(p.max_per_customer)}
                placeholder="Unlimited"
                onChange={e => setEdit(s => ({ ...s, max_per_customer: e.target.value }))}
                inputMode="numeric"
              />
            </Field>
            <Field>
              <FieldLabel>Starts</FieldLabel>
              <Input
                id="promo-edit-starts"
                type="datetime-local"
                defaultValue={toLocalInput(p.starts_at)}
                onChange={e => setEdit(s => ({ ...s, starts_at: e.target.value }))}
              />
              <FieldHint>Your local time. Blank = immediately.</FieldHint>
            </Field>
            <Field>
              <FieldLabel>Expires</FieldLabel>
              <Input
                id="promo-edit-expires"
                type="datetime-local"
                defaultValue={toLocalInput(p.expires_at)}
                onChange={e => setEdit(s => ({ ...s, expires_at: e.target.value }))}
              />
              <FieldHint>Your local time. Blank = never.</FieldHint>
            </Field>
            <Field style={{ gridColumn: '1 / -1' }}>
              <FieldLabel>Plans</FieldLabel>
              <PlanPicker
                id="promo-edit-plans"
                value={editPlans ?? (p.plan_ids || []).map(Number)}
                onChange={setEditPlans}
              />
              <FieldHint>Leave all unticked for every Desk plan. Stays plans must be ticked.</FieldHint>
            </Field>
            <Field style={{ gridColumn: '1 / -1' }}>
              <FieldLabel>Internal note</FieldLabel>
              <Input
                id="promo-edit-description"
                defaultValue={p.description || ''}
                maxLength={NOTE_MAX}
                onChange={e => setEdit(s => ({ ...s, description: e.target.value }))}
              />
            </Field>
          </FieldGrid>
          <Toolbar style={{ marginTop: spacing.sm }}>
            <Btn
              variant="primary"
              id="promo-save"
              disabled={!Object.keys(edit).length && editPlans === null}
              onClick={() => {
                setEditError(null);
                requestAction({
                  title: `Edit ${p.code}`,
                  body: <>These changes govern future redemptions. Nothing already running changes.</>,
                  confirmLabel: 'Save changes',
                  onConfirm: async reason => {
                    const patch: api.PromoInput = {};
                    if (edit.discount_percent !== undefined)
                      patch.discount_percent = edit.discount_percent;
                    if (edit.duration !== undefined) patch.duration = edit.duration;
                    if (edit.duration_in_months !== undefined || edit.duration === 'repeating')
                      patch.duration_in_months =
                        (edit.duration ?? p.duration) === 'repeating'
                          ? limitOut(edit.duration_in_months ?? String(p.duration_in_months ?? ''))
                          : null;
                    if (edit.applies_to_interval !== undefined)
                      patch.applies_to_interval = edit.applies_to_interval;
                    if (edit.customer_eligibility !== undefined)
                      patch.customer_eligibility = edit.customer_eligibility;
                    if (editPlans !== null) patch.plan_ids = editPlans.length ? editPlans : null;
                    if (edit.max_redemptions !== undefined)
                      patch.max_redemptions = limitOut(edit.max_redemptions);
                    if (edit.max_per_customer !== undefined)
                      patch.max_per_customer = limitOut(edit.max_per_customer);
                    if (edit.starts_at !== undefined) patch.starts_at = toInstant(edit.starts_at);
                    if (edit.expires_at !== undefined)
                      patch.expires_at = toInstant(edit.expires_at);
                    if (edit.description !== undefined) patch.description = edit.description;
                    try {
                      await api.updatePromo(p.id, patch, reason);
                      toast.success('Promotion updated');
                      setEdit({});
                      setEditPlans(null);
                      setEditing(false);
                      reload();
                    } catch (e: any) {
                      setEditError(e?.response?.data?.message || 'Could not save those changes.');
                      throw e;
                    }
                  },
                });
              }}
            >
              Save changes
            </Btn>
          </Toolbar>
        </Panel>
      ) : null}

      <Panel title={`Redemptions (${data.redemptions.length})`}>
        <AdminTable<Row>
          columns={REDEMPTION_COLUMNS.filter(c => c.key !== 'code')}
          rows={data.redemptions}
          rowKey={r => String(r.id)}
          emptyLabel="Nobody has used this promotion yet."
          onRowClick={r => nav(`/admin/organizations/${r.organization_id}`)}
        />
      </Panel>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   EVERY REDEMPTION ON THE PLATFORM
   ═══════════════════════════════════════════════════════════════════════════ */

export const RedemptionsScreen: React.FC = () => {
  const nav = useNavigate();
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState('');
  const params = useMemo(
    () => ({ ...(term ? { q: term } : {}), ...(status ? { status } : {}) }),
    [term, status]
  );
  const state = useAdminList<Row>('redemptions', params, { debounce: true });

  return (
    <>
      <NoteBox>
        <strong>confirmed</strong> is the only state that means money moved: an invoice was actually
        paid with the discount on it. <strong>applied</strong> is on the subscription and waiting for
        its first invoice, <strong>pending</strong> is a place held moments ago, and{' '}
        <strong>released</strong> never happened and counts against no limit.
      </NoteBox>
      <Card>
        <CardHead>
          <Toolbar>
            <SearchBox
              value={term}
              onChange={e => setTerm(e.target.value)}
              placeholder="Code, organization or plan…"
              aria-label="Filter redemptions"
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: typography.size.xxs, color: palette.slate }}>Status</span>
              <Select value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">All</option>
                {['pending', 'applied', 'confirmed', 'released'].map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </label>
          </Toolbar>
          <Btn onClick={state.reload}>Refresh</Btn>
        </CardHead>
        <CardBody>
          <AdminTable<Row>
            columns={REDEMPTION_COLUMNS}
            rows={state.items}
            rowKey={r => String(r.id)}
            loading={state.loading}
            loadingMore={state.loadingMore}
            error={state.error}
            total={state.total}
            hasMore={state.hasMore}
            onLoadMore={state.loadMore}
            emptyLabel="No promotion has been redeemed yet."
            onRowClick={r => nav(`/admin/promos/${r.promo_code_id}`)}
          />
        </CardBody>
      </Card>
    </>
  );
};
